// Copyright (c) 2020 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { defineMessages, IntlContext, IntlShape } from "react-intl";

import { Bus, IEnergyMeter, IModule } from "shared/types/parameter";
import { ICloudParameter } from "shared/types/cloudtypes";
import { MainPanel } from "types";
import { IState } from "states/states";
import { IAction } from "actions";
import { getEnergySourceName, getVirtualSiteSourceName, renderVirtualSiteIcon } from "guiutils";
import { isSet } from "shared/utils/utils";

import EnergyMeterModel from "model/energymeter";

interface IEnergyMeterExtProps {
  index: number;
  showSource: boolean;
  showNumber: boolean;
  showModule: boolean;
}

interface IEnergyMeterProps {
  parameter: IEnergyMeter;
  value: number | undefined;
  modules: IModule[] | undefined;
  moduleStatus: boolean | undefined;
  showId: boolean;
  virtualSiteName: string | undefined;
}

const mapStateToProps = (state: IState, ownProps: IEnergyMeterExtProps) => {
  const parameter = state.para.parameter!.energymeter[ownProps.index];
  const moduleIndex =
    parameter?.bus === Bus.MODULE ?
      state.para.parameter?.module?.findIndex((curItem) => curItem.id === parameter.module)
    : undefined;
  const curUser = state.site.userList?.find((curItem) => curItem.secId === state.site.user?.secId);
  const props: IEnergyMeterProps = {
    parameter,
    value: state.data.energymeter[ownProps.index],
    modules: state.para.parameter?.module,
    moduleStatus: isSet(moduleIndex) ? state.data.module[moduleIndex] : true,
    showId: Boolean(curUser?.accessRights.setVirtualParameters),
    virtualSiteName: getVirtualSiteSourceName(
      (state.para.parameter as ICloudParameter | undefined)?.virtualSite?.energymeter,
      ownProps.index,
      state.cached.selectedSite,
      state.site.siteList,
    ),
  };
  return props;
};

interface IEnergyMeterDispatch {}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IEnergyMeterDispatch = {};
  return dispatchProps;
};

const messages = defineMessages<{
  virtualSite: { site: string };
}>({
  virtualSite: {
    id: "This item's data originates from {site}",
  },
});

class EnergyMeterComponent extends React.Component<
  IEnergyMeterExtProps & IEnergyMeterProps & IEnergyMeterDispatch
> {
  static contextType = IntlContext;
  declare context: IntlShape;

  public render() {
    const energymeter = new EnergyMeterModel(
      this.props.parameter,
      this.props.value,
      this.props.moduleStatus,
    );
    const module =
      this.props.parameter.bus === Bus.MODULE ?
        this.props.modules?.find((curItem) => curItem.id === this.props.parameter.module)
      : undefined;

    return (
      <tr className={`energymeteritem ${energymeter.isOffline ? "offline" : ""}`}>
        {this.props.showId && <td className="optional-lg">{this.props.parameter.id}</td>}
        <td>
          {this.props.parameter.name}
          {this.props.virtualSiteName &&
            renderVirtualSiteIcon(
              this.context.formatMessage(messages.virtualSite, {
                site: this.props.virtualSiteName,
              }),
            )}
        </td>
        {this.props.showModule && (
          <td className="optional-md">
            <Link to={`../${MainPanel.MODULES}`} style={{ textDecoration: "none" }}>
              {module?.name}
            </Link>
          </td>
        )}
        {this.props.showSource && (
          <td className="optional-md">{getEnergySourceName(this.props.parameter.energySource)}</td>
        )}
        {this.props.showNumber && <td className="optional-md">{this.props.parameter.number}</td>}
        <td className="value-col">{energymeter.getText()}</td>
      </tr>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(EnergyMeterComponent);
